import { DestroyRef, WritableSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Subject,
  EMPTY,
  debounceTime,
  switchMap,
  finalize,
  catchError,
} from 'rxjs';
import { GroupSettingsService } from '../services';
import { UserWithSelection, GroupPaginationMeta } from '../models';

/** Minimum characters before a backend user search triggers. */
export const USER_SEARCH_MIN_CHARS = 3;

/** Debounce delay (ms) for all backend search inputs across the app. */
export const SEARCH_DEBOUNCE_MS = 300;

export interface UserSearchStore {
  readonly results: WritableSignal<UserWithSelection[]>;
  readonly loading: WritableSignal<boolean>;
  readonly loadingMore: WritableSignal<boolean>;
  readonly paginationMeta: WritableSignal<GroupPaginationMeta | null>;
  /** Trigger a debounced backend search. Pass groupId for is_selected context. */
  search(query: string, groupId?: number): void;
  /** Append the next page to results. */
  loadMore(query: string, groupId?: number): void;
  /** Clear results, loading and meta. */
  reset(): void;
}

/**
 * Creates a reusable reactive user-search pipeline backed by
 * GroupSettingsService.loadUsers(). Encapsulates debounce, loading state,
 * finalize and error handling so components share a single implementation.
 *
 * Call search() to trigger a lookup; reset() to clear state.
 * All returned signals are writable so callers can append (pagination) or
 * pre-set loading when needed.
 */
export function createUserSearch(
  service: GroupSettingsService,
  destroyRef: DestroyRef,
  minChars = USER_SEARCH_MIN_CHARS
): UserSearchStore {
  const subject = new Subject<{ query: string; groupId?: number }>();
  const results = signal<UserWithSelection[]>([]);
  const loading = signal(false);
  const loadingMore = signal(false);
  const paginationMeta = signal<GroupPaginationMeta | null>(null);

  /** Track the last search term so we can skip duplicate no-op searches. */
  let lastSearchQuery: string | null = null;

  subject
    .pipe(
      debounceTime(SEARCH_DEBOUNCE_MS),
      switchMap(({ query, groupId }) => {
        const searchTerm = query.length >= minChars ? query : undefined;
        loading.set(true);
        results.set([]);
        paginationMeta.set(null);
        return service.loadUsers(1, 15, searchTerm, groupId).pipe(
          finalize(() => loading.set(false)),
          catchError(() => EMPTY)
        );
      }),
      takeUntilDestroyed(destroyRef)
    )
    .subscribe(response => {
      results.set(response.data);
      paginationMeta.set(response.meta);
    });

  return {
    results,
    loading,
    loadingMore,
    paginationMeta,
    search(query: string, groupId?: number): void {
      // Queries shorter than minChars all fetch the unfiltered list — normalise for dedup.
      const effectiveQuery = query.length >= minChars ? query : '';
      if (effectiveQuery === lastSearchQuery && results().length > 0) return;
      lastSearchQuery = effectiveQuery;
      // Set loading immediately to avoid showing empty state during debounce + API call
      loading.set(true);
      subject.next({ query, groupId });
    },
    loadMore(query: string, groupId?: number): void {
      const meta = paginationMeta();
      if (!meta || meta.current_page >= meta.last_page || loadingMore()) return;

      const nextPage = meta.current_page + 1;
      const searchTerm = query.length >= minChars ? query : undefined;
      loadingMore.set(true);

      service
        .loadUsers(nextPage, 15, searchTerm, groupId)
        .pipe(
          finalize(() => loadingMore.set(false)),
          catchError(() => EMPTY),
          takeUntilDestroyed(destroyRef)
        )
        .subscribe(response => {
          results.update(prev => [...prev, ...response.data]);
          paginationMeta.set(response.meta);
        });
    },
    reset(): void {
      results.set([]);
      loading.set(false);
      loadingMore.set(false);
      paginationMeta.set(null);
      lastSearchQuery = null;
    },
  };
}
